/* eslint-disable prettier/prettier */
/**
 * Tracking dimensions (CJT-15710): which URL parameter feeds which fixed dimension, per partner.
 *
 * The object below is plain JSON on purpose, so tooling outside this repo (the Snowflake view on
 * DIM_LEAD) can read the same rules: take the object literal after `const mapping: TrackingMapping = `
 * up to the final `};`. Keep it JSON: double quotes, no comments, no trailing commas inside the object.
 * Regexes carry no backslashes (`[0-9]`, `[{]`, `[$]` instead of `\d`, `\{`, `\$`), so the strings need no
 * escaping here, in JSON or in SQL.
 */
import { TrackingMapping } from '../interface/ITrackingMapping';

const mapping: TrackingMapping = {
  "_about": "Source of truth for the tracking dimensions (CJT-15710). sessionLayer maps its landingurl cookie (= Salesforce Landing_URL__c) into these and pushes them to the dataLayer as `tracking`; the Snowflake view on DIM_LEAD applies the same rules. Values are URL-decoded; parameter names and utm_source match case-insensitively.",
  "version": "2026-10-08",
  "macro_pattern": "[{][^}]*[}]|%7[Bb][^&]*?%7[Dd]|[$][{]|%24%7[Bb]|__[A-Z][A-Z0-9_]*__|%%[A-Za-z_]+%%|%25%25[A-Za-z_]+%25%25|[[][A-Z_]+]|%5[Bb][A-Z_]+%5[Dd]",
  "macro_check_skip": [
    "ad_copy"
  ],
  "source_groups": {
    "google": [
      "google",
      "gdn",
      "youtube"
    ],
    "microsoft": [
      "mcrs",
      "bing",
      "performancemax"
    ],
    "meta": [
      "fbih",
      "md9"
    ],
    "taboola": [
      "taboola"
    ],
    "outbrain": [
      "outbrain"
    ],
    "kayzen": [
      "kyz"
    ],
    "bigo": [
      "bgo"
    ],
    "baidu": [
      "bdu"
    ],
    "criteo": [
      "cri"
    ],
    "mgid": [
      "mgid"
    ],
    "tiktok": [
      "tktk"
    ],
    "aduptech": [
      "aduptechnology"
    ],
    "addefend": [
      "addefend"
    ],
    "rtbhouse": [
      "rtbh"
    ],
    "sovendus": [
      "sov"
    ],
    "awin": [
      "awin"
    ]
  },
  "dimensions": {
    "platform_campaign_id": {
      "group": "ad structure",
      "recommended_param": "campaign_id",
      "sources": {
        "google": [
          "gad_campaignid"
        ],
        "microsoft": [
          "campaign"
        ],
        "meta": [
          "utm_campaign_id",
          "utm_id"
        ],
        "kayzen": [
          {
            "param": "campaign",
            "extract": "^([0-9]+)_"
          }
        ],
        "tiktok": [
          "utm_id"
        ],
        "criteo": [
          "utm_id"
        ]
      }
    },
    "platform_adgroup_id": {
      "group": "ad structure",
      "recommended_param": "adgroup_id",
      "sources": {
        "google": [
          "adgroup"
        ],
        "microsoft": [
          "adgroup"
        ],
        "meta": [
          "adset_id",
          "utm_term"
        ],
        "tiktok": [
          "mkt_tool_id"
        ]
      }
    },
    "platform_ad_id": {
      "group": "ad structure",
      "recommended_param": "ad_id",
      "sources": {
        "google": [
          "creative",
          "adid",
          "mkt_tool_id"
        ],
        "microsoft": [
          "adid"
        ],
        "meta": [
          "ad_id",
          "utm_content"
        ],
        "taboola": [
          "utm_creative_ID"
        ],
        "outbrain": [
          "utm_creative_ID"
        ],
        "kayzen": [
          {
            "param": "creative",
            "extract": "^([0-9]+)_"
          }
        ],
        "mgid": [
          "teaser_id"
        ],
        "aduptech": [
          "adid"
        ]
      },
      "unverified": {
        "baidu": "utm_term (numeric, looks like an ad ID)",
        "addefend": "utm_term (numeric)"
      }
    },
    "creative_code": {
      "group": "creative",
      "recommended_param": "aud_c",
      "normalize": "uppercase",
      "extract": "((?:TB|VB|PV)[0-9]+)",
      "flags": "i",
      "sources": {
        "*": [
          "tbvb",
          {
            "param": "utm_campaign",
            "extract": "(?:^|_)((?:tb|vb|pv)[0-9]+)(?:_|$)",
            "flags": "i"
          }
        ],
        "kayzen": [
          {
            "param": "creative",
            "extract": "((?:TB|VB|PV)[0-9]+)",
            "flags": "i"
          }
        ],
        "bigo": [
          {
            "param": "ad_name",
            "extract": "((?:TB|VB|PV)[0-9]+)",
            "flags": "i"
          }
        ],
        "addefend": [
          {
            "param": "utm_content",
            "extract": "((?:TB|VB|PV)[0-9]+)",
            "flags": "i"
          }
        ]
      }
    },
    "ad_copy": {
      "group": "creative",
      "recommended_param": "aud_adcopy",
      "sources": {
        "baidu": [
          "utm_content"
        ],
        "mgid": [
          "title"
        ]
      }
    },
    "publisher": {
      "group": "publisher",
      "recommended_param": "publisher",
      "sources": {
        "google": [
          "placement",
          "utm_publisher_ID"
        ],
        "meta": [
          "placement"
        ],
        "taboola": [
          "utm_publisher_ID"
        ],
        "outbrain": [
          "utm_section_ID"
        ],
        "kayzen": [
          "app"
        ],
        "bigo": [
          "pub_id"
        ],
        "baidu": [
          "publisher_name"
        ],
        "criteo": [
          "utm_publisher_id"
        ],
        "mgid": [
          "widget_id"
        ],
        "aduptech": [
          "sitename"
        ]
      }
    },
    "inventory": {
      "group": "publisher",
      "recommended_param": "inventory",
      "sources": {
        "google": [
          "network"
        ],
        "microsoft": [
          "network"
        ],
        "meta": [
          "site_name"
        ],
        "kayzen": [
          "exchange"
        ]
      }
    },
    "keyword": {
      "group": "search",
      "recommended_param": "keyword",
      "exclude_sources": [
        "mcrs",
        "performancemax"
      ],
      "sources": {},
      "note": "Microsoft native (mcrs) and Performance Max fill `Keyword` with the literal word \"keyword\" or the domain, so keyword isn't read for them."
    },
    "match_type": {
      "group": "search",
      "recommended_param": "match_type",
      "sources": {
        "google": [
          "MatchType"
        ],
        "microsoft": [
          "MatchType"
        ]
      }
    },
    "click_id": {
      "group": "click",
      "recommended_param": "click_id",
      "note": "Platform-native click IDs (gclid, fbclid, msclkid, …) are set by the platform and stay; `click_id` is for partners whose click macro we choose. `click_id_type` = the parameter name the value came from.",
      "sources": {
        "google": [
          "gclid",
          "gbraid",
          "wbraid"
        ],
        "microsoft": [
          "msclkid"
        ],
        "meta": [
          "fbclid"
        ],
        "taboola": [
          "tblci",
          "tbclid"
        ],
        "outbrain": [
          "OutbrainClickId",
          "dicbo"
        ],
        "tiktok": [
          "ttclid"
        ],
        "rtbhouse": [
          "rtbhc"
        ],
        "criteo": [
          "cto_pld"
        ],
        "sovendus": [
          "sovReqToken"
        ],
        "mgid": [
          "click-id",
          "adclid"
        ],
        "awin": [
          "awc"
        ]
      },
      "type_property": "click_id_type"
    },
    "lp_concept": {
      "group": "landing page",
      "recommended_param": "aud_p",
      "sources": {}
    },
    "utm_source": {
      "group": "source",
      "recommended_param": "utm_source",
      "note": "The landing URL's utm_source as it arrived (URL-decoded). No partner fallback: absent when the landing URL has none.",
      "sources": {}
    }
  },
  "resolution": "For each dimension: if the partner's source group has entries, try them in order; then the recommended parameter; then the `*` entries. Take the first present, non-empty value. An entry equal to the recommended parameter is only listed where it must beat a later entry. The raw value is kept as-is, unfilled macros included. `exclude_sources`: utm_source values (case-insensitive) for which the dimension is never read. A dimension-level `extract` (+ `flags`) applies to every entry without its own extract, the recommended parameter included; a value it doesn't match is skipped.",
  "error_property": {
    "name": "tracking_errors",
    "value": "list of `<dimension>:<param>` whose value matches macro_pattern, e.g. [\"platform_ad_id:utm_content\"]; absent when clean"
  }
};

export default mapping;
